import { clsx } from 'clsx'

import { Link, Text } from '@buffer-mono/popcorn'

import { parseThreadsLinks } from '~publish/legacy/utils/channels/threads/helpers'
import type { Service } from '~publish/pages/Community/graphqlEnums'

import { ExpandableEngagementText } from '../../components/ExpandableEngagementText'
import {
  MentionedChannelText,
  mentionedChannelClassName,
} from '../../components/MentionedChannelText'
import { isMentionedChannelText } from '../../util/getMentionedChannelTextParts'
import type { MentionedChannel } from '../../util/getMentionedChannelTextParts'
import { useCommentData } from '../context/CommentContext'

import styles from './CommentText.module.css'

import type { JSX, ReactNode } from 'react'

const NO_CHANNEL: MentionedChannel = {}

/**
 * Threads text with its linkified `@handles`, hashtags and URLs. Mirrors
 * `LinkifiedText`'s output, and highlights the channel's name both in its
 * own `@handle` link and in the text between links.
 */
const ThreadsText = ({
  text,
  channel,
  className,
}: {
  text: string
  channel: MentionedChannel
  className: string
}): JSX.Element => {
  const elements: ReactNode[] = []
  let offset = 0
  for (const link of parseThreadsLinks(text)) {
    const [start, end] = link.indices
    if (start >= text.length) continue
    if (start > offset) {
      elements.push(
        <MentionedChannelText
          key={`text-${offset}`}
          text={text.slice(offset, start)}
          channel={channel}
        />,
      )
    }
    elements.push(
      <Link
        key={`link-${start}`}
        href={link.url}
        target="_blank"
        rel="noopener noreferrer"
        className={clsx(
          link.className,
          isMentionedChannelText(link.displayString, channel) &&
            mentionedChannelClassName,
        )}
      >
        {link.displayString}
      </Link>,
    )
    offset = end
  }
  if (offset < text.length) {
    elements.push(
      <MentionedChannelText
        key={`text-${offset}`}
        text={text.slice(offset)}
        channel={channel}
      />,
    )
  }

  return (
    <Text
      translate="no"
      style={{ whiteSpace: 'pre-wrap' }}
      size="md"
      color="inherit"
      className={className}
    >
      {elements}
    </Text>
  )
}

export const TextContent = ({
  text,
  serviceType,
  className,
  channel = NO_CHANNEL,
}: {
  text: string
  serviceType: Service
  className?: string
  /** The channel whose own name gets highlighted; omit for no highlight. */
  channel?: MentionedChannel
}): JSX.Element | null => {
  if (!text) {
    return null
  }

  if (serviceType === 'threads') {
    return (
      <ThreadsText
        text={text}
        channel={channel}
        className={clsx(styles.commentText, className)}
      />
    )
  }

  const displayText =
    serviceType === 'youtube' ? text.replace(/❤(?!️)/g, '❤️') : text

  return (
    <Text className={className} translate="no">
      <MentionedChannelText text={displayText} channel={channel} />
    </Text>
  )
}

export const CommentText = ({
  channel,
}: {
  /** The channel whose own name gets highlighted; omit for no highlight. */
  channel?: MentionedChannel
}): JSX.Element | null => {
  const comment = useCommentData()

  if (!comment.text) {
    return null
  }

  return (
    <ExpandableEngagementText>
      <TextContent
        text={comment.text}
        serviceType={comment.serviceType}
        className={clsx(comment.isDeleted && styles.deleted)}
        channel={comment.isDeleted ? undefined : channel}
      />
    </ExpandableEngagementText>
  )
}
